import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery } from '@tanstack/react-query';

import { GENDERS } from 'components/intake/PatientInfoSection';
import Button from 'components/shared/Button';
import Foldout from 'components/shared/Foldout';
import DatePicker from 'components/shared/form/DatePicker';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import { useExtensionPresence } from 'hooks/useExtensionPresence';
import { usePortalName } from 'hooks/usePortalConnections';
import useWorkflow from 'hooks/useWorkflow';
import { parseDate, simpleDashDate, simpleDateAtTime } from 'lib/date';
import Workflow from 'models/Workflow';
import { clientPortalMappingQueryKeys, indexClientPortalMappings } from 'services/api/portalMappings';
import { archivePortalScript, indexPortalScripts } from 'services/api/portalScripts';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, BodySmallBold, H3 } from 'styles/typography';
import CaretLeft from 'svg/CaretLeft';
import CaretRight from 'svg/CaretRight';
import XIcon from 'svg/XIcon';

import { actionKindMeta, labelForValue } from '../actionConfig';
import { collectedFacilities, FacilityOption, SECTION_LABELS, VALUE_NAMES } from '../constants';
import {
  facilityMappingState,
  FacilityPicks,
  facilityPicksComplete,
  facilityResolutions,
  NO_FACILITY_PICKS,
} from '../FacilityMappingReview';

import { ACTION_KINDS } from './constants';
import FacilitySection, {
  facilityAutoMatches,
  preMappedFacilities,
  preMappingComplete,
  stagedFacilityStrings,
} from './FacilitySection';
import { outstandingFacilityMapping, outstandingRetrieval, resolveFields } from './fieldResolver';
import { useManualLaunch } from './ManualLaunchProvider';
import TaskSection, { FieldRow, ReadOnlyValue } from './TaskSection';
import {
  buildActionCompletions,
  buildValues,
  everySectionComplete,
  isFieldSatisfied,
  isSectionComplete,
  pendingValue,
  sectionFields,
  TaskSlideoutForm,
} from './taskSubmit';

/**
 * The task slideout — the redesign's paradigm shift (OLIO-3022).
 *
 * The same run the step view drew as a progress rail plus one card per action is drawn here as
 * SECTIONS OF PROPERTIES: fields grouped by what they describe rather than by which action
 * produced them. Somebody filling in a last name and resolving a facility is satisfying two
 * different actions on two different kinds underneath, and must not be able to tell — which is
 * why every field routes through one resolver and every answer commits in one request.
 *
 * Two sections ship. Insurance Information is drawn in the design and PUNTED (nothing captures
 * those values yet), so it is absent rather than empty.
 */

interface Props {
  workflowId: string | null;
  // The traversal order, snapshotted from the list as the person opened it. A snapshot rather
  // than a live read: the list refetches continuously, and arrows that reordered themselves
  // under a reader would land them on a different task than the one they aimed at.
  siblingWorkflowIds?: string[];
  onNavigate?: (workflowId: string) => void;
  onClose: () => void;
}

export default function TaskSlideout({ workflowId, siblingWorkflowIds = [], onNavigate, onClose }: Props) {
  return (
    <Foldout
      open={!!workflowId}
      setOpen={onClose}
      closeOnBackdropClick
      width='min(96%, 560px)'
      data-testid='task-slideout'>
      {workflowId && (
        <TaskSlideoutBody
          // Remounted per task: the form holds what the person has typed, and carrying it across
          // a Next Task press would offer one patient's demographics for another's referral.
          key={workflowId}
          workflowId={workflowId}
          siblingWorkflowIds={siblingWorkflowIds}
          onNavigate={onNavigate}
          onClose={onClose}
        />
      )}
    </Foldout>
  );
}

function TaskSlideoutBody({
  workflowId,
  siblingWorkflowIds,
  onNavigate,
  onClose,
}: Props & { workflowId: string; siblingWorkflowIds: string[] }) {
  const {
    workflow,
    isLoading,
    isError,
    submitActionCompletions,
    isSubmittingCompletions,
    restartActions,
    isRestartingActions,
  } = useWorkflow(workflowId);
  const portalName = usePortalName();
  const { addToast } = useToastActions();
  const { present } = useExtensionPresence();
  const { activeWorkflowId, launchingWorkflowId, unlockedWorkflowIds, launch } = useManualLaunch();
  const { facilities: allowedFacilities } = useAllowedFacilities();

  // The client's saved mappings for this portal, so a captured facility string the client
  // already knows starts resolved — the same verdict the mapping step's bag would reach.
  const workflowSourceSystem = workflow?.valueFor('source_system')?.value ?? '';
  const mappingsQuery = useQuery({
    queryKey: clientPortalMappingQueryKeys.list(workflowSourceSystem),
    queryFn: ({ signal }) => indexClientPortalMappings(workflowSourceSystem, signal),
    enabled: !!workflowSourceSystem,
  });

  const [values, setValues] = useState<Record<string, string>>({});
  const [picks, setPicks] = useState<FacilityPicks>(NO_FACILITY_PICKS);
  const [entered, setEntered] = useState<(FacilityOption | null)[]>([null]);
  const [submitted, setSubmitted] = useState(false);
  // `Manually input data` on the launch pane: skip the portal and type it in — the same escape
  // hatch the old retrieval card offered.
  const [manualEntry, setManualEntry] = useState(false);

  // `Re-record script`, ported from the old retrieval card: archive the saved script so the next
  // portal open records a fresh one. Archiving happens up front rather than after a successful
  // recording — a recording the person abandons must not leave the script they rejected live.
  const reRecord = useMutation({
    mutationFn: async () => {
      const sourceSystem = workflow?.valueFor('source_system')?.value ?? '';
      const scripts = await indexPortalScripts(sourceSystem);
      if (scripts.length === 0) return false;
      await archivePortalScript(scripts[0].id);
      return true;
    },
    onSuccess: (archived) =>
      addToast({
        text: archived
          ? 'The saved script was retired. Opening the portal will record a fresh one.'
          : 'There is no saved script to replace. Opening the portal will record one.',
      }),
    onError: () => addToast({ text: 'Could not replace the saved script.' }),
  });

  const index = siblingWorkflowIds.indexOf(workflowId);
  const previousId = index > 0 ? siblingWorkflowIds[index - 1] : undefined;
  const nextId = index >= 0 && index < siblingWorkflowIds.length - 1 ? siblingWorkflowIds[index + 1] : undefined;

  const header = (
    <Header>
      <HeaderText>
        <H3 data-testid='task-slideout-title'>{workflow?.patientName ?? 'Task'}</H3>
        {workflow?.createdAt && (
          <Created data-testid='task-slideout-created'>
            Created {simpleDateAtTime(workflow.createdAt)}
            {sourceLabel(workflow, portalName)}
          </Created>
        )}
      </HeaderText>
      <HeaderActions>
        <NavButton
          type='button'
          disabled={!previousId}
          onClick={() => previousId && onNavigate?.(previousId)}
          data-testid='task-slideout-prev'>
          <CaretLeft height={10} />
          Prev Task
        </NavButton>
        <NavButton
          type='button'
          disabled={!nextId}
          onClick={() => nextId && onNavigate?.(nextId)}
          data-testid='task-slideout-next'>
          Next Task
          <CaretRight height={10} />
        </NavButton>
        <CloseButton type='button' aria-label='Close' onClick={onClose} data-testid='task-slideout-close'>
          <XIcon width={10} height={10} />
        </CloseButton>
      </HeaderActions>
    </Header>
  );

  if (isLoading || !workflow) {
    return (
      <Panel>
        {header}
        <Message data-testid={isError ? 'task-slideout-error' : 'task-slideout-loading'}>
          {isError ? 'We couldn’t load this task.' : 'Loading…'}
        </Message>
      </Panel>
    );
  }

  const outcome = workflow.valueFor('portal_automation_outcome')?.value ?? '';
  if (outcome) {
    return (
      <Panel>
        {header}
        <SuccessPanel workflow={workflow} outcome={outcome} />
      </Panel>
    );
  }

  const fields = resolveFields(workflow);
  const mapping = outstandingFacilityMapping(workflow);
  const retrieval = outstandingRetrieval(workflow);

  // The launch gate, moved here from the task card: while the retrieval is waiting on a person
  // AND this browser can open the portal (manual/extension mode, extension present, resolvable
  // pointer), the pane leads with `Open in <portal>` instead of the fields. The HUD's ack
  // unlocks the task for the session (`unlockedWorkflowIds`), and `Manually input data` is the
  // same escape hatch the old retrieval card had. Headless runs and extension-less browsers
  // never see this — the form is their only path.
  const pointer = workflow.valueFor('retrieval_pointer')?.value ?? '';
  const launchable =
    !!retrieval &&
    // A retrieval that already carries staged outputs is a REVIEW moment — a sweep or an
    // earlier run captured the details, and sending the person back into the portal would
    // redo work a machine already did. The form (pre-filled from the staged capture) leads.
    !retrieval.hasStagedCapture &&
    /^https?:\/\//.test(pointer) &&
    ['manual', 'extension'].includes(workflow.retrievalMode ?? 'manual') &&
    present === true;

  // A failed automated retrieval leads with Retry, for headless AND extension alike:
  // restarting the errored action re-enters dispatch, so a headless run re-replays with the
  // saved recording and an extension run re-arms the Open-in-portal pane. Entering the
  // details by hand stays available as the fallback, never the lead.
  const retriable =
    !!retrieval && retrieval.isErrored && ['headless', 'extension'].includes(workflow.retrievalMode ?? 'manual');

  if (retriable && !manualEntry) {
    const label = portalName(workflow.valueFor('source_system')?.value ?? '') || 'the portal';

    return (
      <Panel>
        {header}
        <LaunchPane data-testid='task-slideout-errored'>
          <FailureNote data-testid='task-slideout-errored-note'>Automated retrieval from {label} failed.</FailureNote>
          <Button
            size='small'
            loading={isRestartingActions}
            data-testid='task-slideout-retry'
            onClick={() => restartActions({ actionIds: [retrieval!.id] })}>
            Retry retrieval
          </Button>
          <LaunchLinks>
            <LinkButton type='button' onClick={() => setManualEntry(true)} data-testid='task-slideout-manual-entry'>
              Manually input data
            </LinkButton>
          </LaunchLinks>
        </LaunchPane>
      </Panel>
    );
  }

  if (launchable && !unlockedWorkflowIds.has(workflowId) && !manualEntry) {
    const sourceSystem = workflow.valueFor('source_system')?.value ?? '';
    const label = portalName(sourceSystem) || 'the portal';
    const opened = activeWorkflowId === workflowId;
    // The extension permits exactly one manual job browser-wide; disabling here states that
    // rule before the click rather than after it.
    const blocked = activeWorkflowId !== null && !opened;

    return (
      <Panel>
        {header}
        <LaunchPane data-testid='task-slideout-launch'>
          <Button
            size='small'
            loading={launchingWorkflowId === workflowId}
            disabled={opened || blocked}
            data-testid='task-slideout-launch-button'
            onClick={() =>
              launch({
                workflowId,
                actionId: retrieval!.id,
                entryUrl: pointer,
                sourceSystem,
                portalPatientName: workflow.portalPatientName,
              })
            }>
            {opened ? `Opened in ${label}` : `Open in ${label}`}
          </Button>
          <LaunchLinks>
            {/* Only extension mode replays a recorded script — a manual-mode open is a plain
                visit with the HUD alongside, so there is nothing to re-record. */}
            {workflow.retrievalMode === 'extension' && (
              <LinkButton
                type='button'
                disabled={reRecord.isPending}
                onClick={() => reRecord.mutate()}
                data-testid='task-slideout-rerecord'>
                Re-record script
              </LinkButton>
            )}
            <LinkButton type='button' onClick={() => setManualEntry(true)} data-testid='task-slideout-manual-entry'>
              Manually input data
            </LinkButton>
          </LaunchLinks>
        </LaunchPane>
      </Panel>
    );
  }

  // The pre-mapping review (OLIO-3022): a retrieval that staged facility strings is answered
  // with the mapping surface up front. The pick each string starts with comes from the saved
  // mappings and the user's own facility names; the person's gestures live in `picks`.
  const preStrings = !mapping && retrieval ? stagedFacilityStrings(retrieval) : [];
  const autoMatch = facilityAutoMatches(
    preStrings,
    mappingsQuery.data ?? [],
    allowedFacilities.map((group) => ({ id: group.id, name: group.name }))
  );
  const preMapping = preStrings.length > 0;

  // Only what the person actually entered. `collectedFacilities` returns `[]` when they have
  // entered nothing, and `[]` is truthy — carrying it would read as "the answer is none", blanking
  // a facility the script captured and overwriting it on submit.
  const enteredFacilities = !mapping && retrieval && !preMapping ? collectedFacilities(entered) : undefined;

  const form: TaskSlideoutForm = {
    values,
    // Exactly one of these is set: the mapping step owns the facility answer while it is
    // outstanding, and sending a `facilities` collection alongside it would hand the run a
    // second, conflicting one.
    facilityResolutions: mapping ? facilityResolutions(facilityMappingState(mapping), picks) : undefined,
    facilityComplete: mapping
      ? facilityPicksComplete(facilityMappingState(mapping), picks)
      : preMapping
        ? preMappingComplete(preStrings, picks, autoMatch)
        : undefined,
    facilities: preMapping
      ? preMappedFacilities(preStrings, picks, autoMatch)
      : enteredFacilities?.length
        ? enteredFacilities
        : undefined,
  };

  const completions = buildActionCompletions(workflow, fields, form);
  const correctedValues = buildValues(fields, form);
  const hasCorrections = Object.keys(correctedValues).length > 0;
  // A submit can be corrections alone: with no action outstanding there is nothing to complete,
  // and fixing a bad capture is still a real gesture.
  const ready =
    everySectionComplete(fields, form) && (completions.length > 0 || hasCorrections) && !isSubmittingCompletions;

  // `submitted` flips when the mutation SETTLES successfully, not on click: flipping early
  // would show the pending panel over an unsaved form, and a 422 would strand it there with
  // the person's typed corrections off screen.
  const submit = () => {
    submitActionCompletions(
      {
        actionCompletions: completions,
        values: hasCorrections ? correctedValues : undefined,
      },
      { onSuccess: () => setSubmitted(true) }
    );
  };

  // Disposition cannot happen inside the submit: the response settles one evaluator pass, and
  // reaching DisposePortalReferral needs several (plus the AI callbacks in between). So a
  // successful submit lands here, and the outcome arrives on the socket nudge or the poll.
  if (submitted && completions.length === 0) {
    return (
      <Panel>
        {header}
        <Message data-testid='task-slideout-pending'>
          <Body>Finishing this referral…</Body>
          <BodySmall>Olio is working through the rest of this task. This updates on its own.</BodySmall>
        </Message>
      </Panel>
    );
  }

  return (
    <Panel>
      {header}
      <Sections>
        <TaskSection
          title={SECTION_LABELS.demographics}
          complete={isSectionComplete('demographics', fields, form)}
          description='Review the extracted patient demographics below and complete any missing fields or edit any fields that seem incorrect'
          testId='task-section-demographics'>
          {sectionFields('demographics').map((field) => {
            const state = fields.get(field.name);
            const label = titleCase(labelForValue(workflow, field.name, field.label));
            const satisfied = isFieldSatisfied(state, form);
            const current = String(pendingValue(state, form) ?? '');

            return (
              <FieldRow key={field.name} label={label} present={satisfied} error='This field is missing and required'>
                {!state?.editable ? (
                  <ReadOnlyValue value={displayValue(field.type, current)} />
                ) : field.type === 'date' ? (
                  <DatePicker
                    selected={parseDate(bareDate(current)) ?? null}
                    onChange={(date: Date | null) =>
                      setValues((prev) => ({ ...prev, [field.name]: simpleDashDate(date) ?? '' }))
                    }
                    $fullWidth
                    placeholder='MM/DD/YYYY'
                  />
                ) : field.type === 'select' ? (
                  <div data-testid={`task-field-input-${field.name}`}>
                    <Select
                      inputId={`task-field-input-${field.name}`}
                      placeholder='Select'
                      options={GENDERS}
                      value={GENDERS.find((option) => option.value === genderValue(current)) ?? null}
                      onChange={(option) => setValues((prev) => ({ ...prev, [field.name]: option?.value ?? '' }))}
                      getOptionValue={(option) => option.value}
                      getOptionLabel={(option) => option.label}
                    />
                  </div>
                ) : (
                  <Input
                    value={current}
                    onChange={(event) => setValues((prev) => ({ ...prev, [field.name]: event.target.value }))}
                    data-testid={`task-field-input-${field.name}`}
                  />
                )}
              </FieldRow>
            );
          })}
        </TaskSection>

        <TaskSection
          title={SECTION_LABELS.facility}
          complete={isSectionComplete('facility', fields, form)}
          description={
            mapping || preMapping
              ? 'Review the extracted facility below and ensure it is correct. If needed, map the extracted facility name to the facility name used in Olio.'
              : undefined
          }
          testId='task-section-facility'>
          <FacilitySection
            workflow={workflow}
            // Singular even when the catalog says "Collected facilities": several facilities
            // are supported QUIETLY (FacilitySection's rule), and a plural label is the one
            // affordance that would announce it.
            label={titleCase(labelForValue(workflow, VALUE_NAMES.FACILITIES, 'Facility')).replace(
              /\bFacilities\b/,
              'Facility'
            )}
            present={isFieldSatisfied(fields.get(VALUE_NAMES.FACILITIES), form)}
            mapping={mapping}
            retrieval={retrieval}
            picks={picks}
            onPicksChange={setPicks}
            entered={entered}
            onEnteredChange={setEntered}
            stagedStrings={preStrings}
            autoMatch={autoMatch}
          />
        </TaskSection>
      </Sections>

      <Footer>
        {/* One generic label, both outcomes. Which one happens is the api's call at dispatch —
            it matches against existing referrals — so a button promising `Create` would be
            wrong exactly when the merge it triggered was the useful thing. */}
        <Button
          size='small'
          disabled={!ready}
          loading={isSubmittingCompletions}
          onClick={submit}
          data-testid='task-slideout-submit'>
          Create/Merge Referral
        </Button>
      </Footer>
    </Panel>
  );
}

// The provenance line's second half. The run carries the portal it came from, not the scan that
// spawned it — a retrieval holds no pointer back to its sweep — so this names the portal and
// stops there rather than claiming a scan it cannot verify.
function sourceLabel(workflow: Workflow, portalName: (system?: string | null) => string): string {
  const system = workflow.valueFor('source_system')?.value;
  const name = system ? portalName(system) : '';
  return name ? ` (via ${name})` : '';
}

// The catalog labels arrive sentence-cased (`First name`); the design draws them title-cased
// (`First Name`, `Date of Birth`). Display concern only, so it lives here, not in the registry.
const MINOR_WORDS = new Set(['of', 'and', 'or', 'the', 'a', 'an', 'in', 'on', 'to', 'for']);
function titleCase(label: string): string {
  return label
    .split(' ')
    .map((word, index) =>
      index > 0 && MINOR_WORDS.has(word.toLowerCase())
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
}

function displayValue(type: string, value: string): string {
  if (!value) return '';
  if (type === 'select') return GENDERS.find((option) => option.value === genderValue(value))?.label ?? value;
  return value;
}

// Staged captures arrive as the portal wrote them — a DOB carrying an age annotation
// ("02/04/1943 (83 yr)"), a gender as a single letter ("M"). An unedited value submits back
// verbatim (the api normalizes), so these adjust DISPLAY only: without them the controls render
// blank over a real capture, an empty-looking field wearing a green mark.
function bareDate(value: string): string {
  const stripped = value.replace(/\s*\([^)]*\)\s*$/, '');
  // parseDate is ISO-only; portals write US dates.
  const us = stripped.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  return stripped;
}

function genderValue(value: string): string {
  const lowered = value.trim().toLowerCase();
  const match = GENDERS.find((option) => option.value === lowered || option.label.toLowerCase() === lowered);
  if (match) return match.value;
  if (lowered === 'm') return 'male';
  if (lowered === 'f') return 'female';
  if (lowered === 'u') return 'unknown';
  return value;
}

// Terminal state. The copy differs between the two outcomes because the outcomes differ: merging
// put this portal's information onto a referral Olio already had, and a person told "created"
// would go looking for a second referral that does not exist.
function SuccessPanel({ workflow, outcome }: { workflow: Workflow; outcome: string }) {
  const meta = actionKindMeta(ACTION_KINDS.dispose);
  const referralId = workflow.valueFor('disposed_workflow_id')?.value ?? '';
  const merged = outcome === 'merged';
  const heading = meta?.resultLabels?.[outcome] ?? (merged ? 'Merged into an existing referral' : 'Referral created');
  const path = meta?.resultLink?.path?.(referralId) ?? `/inbound-referrals/${referralId}`;

  return (
    <Success data-testid={merged ? 'task-slideout-outcome-merged' : 'task-slideout-outcome-created'}>
      <BodySmallBold>{heading}</BodySmallBold>
      <BodySmall>
        {merged
          ? 'This portal task’s details were added to a referral Olio already had, and the task is finished.'
          : 'Olio created a new referral from this portal task, and the task is finished.'}
      </BodySmall>
      {referralId && (
        <ReferralLink to={path} data-testid='task-slideout-view-referral'>
          View Referral
        </ReferralLink>
      )}
    </Success>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 12px;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Created = styled(BodySmall)`
  color: ${colors.black50};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 13px;
  font-weight: 600;
  color: ${colors.black100};
  cursor: pointer;

  &:disabled {
    color: ${colors.black25};
    cursor: default;
  }
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  padding: 4px;
  border: none;
  background: none;
  color: ${colors.black75};
  cursor: pointer;
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
`;

// The launch gate, drawn centered in the pane as designed.
const LaunchPane = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 24px;
  flex: 1;
`;

const LaunchLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const LinkButton = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 13px;
  color: ${colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;

  &:disabled {
    color: ${colors.black25};
    cursor: default;
  }
`;

const FailureNote = styled.p`
  color: ${colors.black75};
  text-align: center;
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 48px 24px;
  text-align: center;
  color: ${colors.black75};
`;

const Success = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin: 24px;
  padding: 24px;
  border: 1px solid ${colors.green25};
  border-radius: 8px;
  background: ${colors.green10};
`;

const ReferralLink = styled(Link)`
  font-size: 14px;
  color: ${colors.blue100};
`;
